import React, { useState } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useDeleteDeletedEvent,
  useDeletedEventData,
  useRestoreDeletedEvent,
} from "../../../hooks/useDeletedEvent";
import { useDivisionNameById } from "../../../hooks/useDivision";
import { useTeamNameById } from "../../../hooks/useTeam";
import { useUser } from "../../../hooks/useUser";
import EventStatusBadge from "../../EventDetails/EventStatus";
import DataTable from "../../shared/DataTable";
import { usePermission } from "../../../hooks/usePermission";
import { DeleteEventsPermission } from "../../../constants/permissions";

const DivisionNameCell = ({ divisionId }) => {
  const { data: divisionName } = useDivisionNameById(divisionId);
  return divisionName || "";
};

const TeamNameCell = ({ teamId }) => {
  const { data: teamName } = useTeamNameById(teamId);
  return teamName || "";
};

export const DeletedEventsTable = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const { data: deletedEvents } = useDeletedEventData();
  const { data: usersData } = useUser();
  const { mutate: restoreEvent } = useRestoreDeletedEvent();
  const { mutate: deleteEvent } = useDeleteDeletedEvent();

  const handleClick = (event, eventData) => {
    setAnchorEl(event.currentTarget);
    setSelectedEvent(eventData);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const { data: canDeleteEvents } = usePermission({
    permissionName: DeleteEventsPermission,
  });

  const ActionMenu = ({ row }) => (
    <>
      <IconButton onClick={(e) => handleClick(e, row)} size="small">
        <MoreVertIcon />
      </IconButton>
      {canDeleteEvents && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem className="w-40">
            <button
              data-treack="restore-deleted-event-button"
              data-module="Events"
              onClick={() => {
                restoreEvent(selectedEvent.id, {
                  onError: (error) =>
                    toast.error(error.message || "Failed to restore event"),
                });
                handleClose();
              }}
              className="text-blue-600"
            >
              Restore
            </button>
          </MenuItem>
          <MenuItem>
            <button
              data-treack="delete-deleted-event-button"
              data-module="Events"
              onClick={() => {
                deleteEvent(selectedEvent.id);
                handleClose();
              }}
              className="text-red-600"
            >
              Delete
            </button>
          </MenuItem>
        </Menu>
      )}
    </>
  );

  const columns = [
    {
      name: "Event Id",
      selector: (row) => row.id,
      cell: (row) => <span className="text-blueColor">{row.id}</span>,
      width: "150px",
    },
    {
      name: "Event Name",
      selector: (row) => row.name,
      cell: (row) => <span className="text-blueColor">{row.name}</span>,
      width: "200px",
    },
    {
      name: "Creator",
      selector: (row) =>
        usersData?.find((user) => user.id === row.created_by)?.first_name,
      cell: (row) => (
        <span className="text-blueColor">
          {usersData?.find((user) => user.id === row.created_by)?.first_name}
        </span>
      ),
      width: "150px",
    },
    {
      name: "Division",
      selector: (row) => {
        return <DivisionNameCell divisionId={row.division_id} />;
      },
      cell: (row) => (
        <span className="text-blueColor">
          <DivisionNameCell divisionId={row.division_id} />
        </span>
      ),
      width: "200px",
    },
    {
      name: "Team",
      selector: (row) => {
        return <TeamNameCell teamId={row.team_id} />;
      },
      cell: (row) => (
        <span className="text-blueColor">
          <TeamNameCell teamId={row.team_id} />
        </span>
      ),
      width: "150px",
    },
    {
      name: "Promo Num",
      selector: (row) => row.promo_number,
      cell: (row) => <span className="text-blueColor">{row.promo_number}</span>,
      width: "150px",
    },
    {
      name: "Start Date",
      selector: (row) => row.start_date,
      cell: (row) => <span className="text-blueColor">{row.start_date}</span>,
      width: "150px",
    },
    {
      name: "Status",
      cell: (row) => <EventStatusBadge size="xs" status={row.status} />,
      width: "150px",
    },
    {
      name: "Actions",
      cell: (row) => <ActionMenu row={row} />,
      width: "150px",
    },
  ];

  return (
    <div className="container my-5">
      <div className="bg-white shadow-md my-4 w-1/2 mx-auto">
        <div className="container mx-auto">
          <nav className="flex justify-center space-x-8">
            <button
              className={`px-6 py-4 text-lg font-medium relative hover:bg-gray-50 transition-all duration-200
                                ${
                                  window.location.pathname === "/deleted-events"
                                    ? "text-purpleColor border-b-2 border-purpleColor"
                                    : "text-gray-500 hover:text-purpleColor "
                                }`}
              onClick={() => navigate("/deleted-events")}
            >
              <div className="flex items-center gap-2">
                <i className="fas fa-trash-restore"></i>
                <span>Deleted Events</span>
              </div>
              <div
                className={`absolute bottom-0 left-0 w-full h-0.5 ${
                  window.location.pathname === "/deleted-events"
                    ? "bg-purpleColor"
                    : "bg-transparent"
                }`}
              ></div>
            </button>
            <button
              className={`px-6 py-4 text-lg font-medium relative hover:bg-gray-50 transition-all duration-200
                                ${
                                  window.location.pathname ===
                                  "/deleted-subscribers"
                                    ? "text-purpleColor"
                                    : "text-gray-500 hover:text-purpleColor"
                                }`}
              onClick={() => navigate("/deleted-subscribers")}
            >
              <div className="flex items-center gap-2">
                <i className="fas fa-users-slash"></i>
                <span>Deleted Attendees</span>
              </div>
              <div
                className={`absolute bottom-0 left-0 w-full h-0.5 ${
                  window.location.pathname === "/deleted-subscribers"
                    ? "bg-purpleColor"
                    : "bg-transparent"
                }`}
              ></div>
            </button>
          </nav>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={deletedEvents || []}
        searchFields={["id", "name", "promo_number"]}
        searchPlaceholder="Search events..."
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 30]}
      />
    </div>
  );
};
